// src/pages/TrusteePortalPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../../gmanewstyle.module.css";
import logoimage from "../assets/Group -2.png";
import { format } from "date-fns";
import PortalSidebar, {
  usePortalTab,
} from "../PortalSidebar/PortalSidebar";
// Reusable Pagination component
const Pagination = ({
  currentPage,
  setCurrentPage,
  totalRecords,
  recordsPerPage,
}) => {
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = Math.min(startIndex + recordsPerPage, totalRecords);
  return (
    <div className={styles.pagination}>
      <button
        className={`${styles.pageBtn} ${styles.pageBtnPrimary}`}
        onClick={() => setCurrentPage(currentPage - 1)}
        disabled={currentPage === 1}
      >
        Previous
      </button>
      <span className={styles.pageMeta}>
        {totalRecords === 0
          ? "0 - 0 of 0"
          : `${startIndex + 1} - ${endIndex} of ${totalRecords}`}
      </span>
      <button
        className={`${styles.pageBtn} ${styles.pageBtnPrimary}`}
        onClick={() => setCurrentPage(currentPage + 1)}
        disabled={endIndex >= totalRecords}
      >
        Next
      </button>
    </div>
  );
};

const TrusteePortalPage = () => {
  // sidebar tabs
  const [mainFilter, setMainFilter] = usePortalTab("trustee");
  // approvals tabs
  const [subFilter, setSubFilter] = useState("active");

  const [approvals, setApprovals] = useState({});
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  const [searchTerm, setSearchTerm] = useState("");
  const [dateMode, setDateMode] = useState("single");
  const [dateFilter, setDateFilter] = useState({ from: "", to: "" });
  const [pendingDateFilter, setPendingDateFilter] = useState({
    from: "",
    to: "",
  });
  const [approvalTypeFilter, setApprovalTypeFilter] = useState("all");
  const [expandedBuilding, setExpandedBuilding] = useState(null);
  const [allowedBuildings, setAllowedBuildings] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 5;

  const [userAccess, setUserAccess] = useState([]);
  const [loadingAccess, setLoadingAccess] = useState(true);

  const navigate = useNavigate();

  const approvalTypeOptions = [
    "All",
    "AOD",
    "Payment",
    "Maintenance Quote Selection",
    "Conduct Letter",
    "Conduct Fine",
    "Budget",
    "Minutes",
    "Legal",
    "Maintenance work approval",
  ];

  const fetchAllowedBuildings = async () => {
    const token = localStorage.getItem("token");
    const contactId = localStorage.getItem("contact_id");
    if (!token || !contactId) return;

    try {
      const res = await fetch(
        "https://portal.gmacc.co.za/api/trustee/trustee-list/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ contact_id: contactId }),
        }
      );

      const data = await res.json();
      if (res.ok && data.building_guids) {
        setAllowedBuildings(data.building_guids);
      } else {
        setAllowedBuildings([]);
      }
    } catch (err) {
      console.error("Error loading trustee access:", err);
      setAllowedBuildings([]);
    }
  };

  const groupByBuilding = (data) => {
    const grouped = {};
    data.forEach((item) => {
      const buildingName =
        item.building ||
        item[
          "_cr446_relatedbuilding_value@OData.Community.Display.V1.FormattedValue"
        ] ||
        "Unassigned";

      let buildingGuid = item.buildingGuid || item._cr446_relatedbuilding_value;
      if (!buildingGuid && item._cj_building_value) {
        buildingGuid = item._cj_building_value;
      }

      if (!grouped[buildingName]) {
        grouped[buildingName] = {
          buildingGuid: buildingGuid || null,
          items: [],
        };
      }
      grouped[buildingName].items.push(item);
    });
    return grouped;
  };

  const formatDateSafe = (rawDate) => {
    try {
      return format(new Date(rawDate), "dd MMM yyyy HH:mm");
    } catch {
      return rawDate;
    }
  };

  const matchesDateFilter = (dateStr) => {
    if (!dateFilter.from) return true;
    const d = format(new Date(dateStr), "yyyy-MM-dd");
    if (dateMode === "single") {
      return d === format(new Date(dateFilter.from), "yyyy-MM-dd");
    }
    return d >= dateFilter.from && d <= (dateFilter.to || dateFilter.from);
  };

  const matchesTypeFilter = (type) => {
    return approvalTypeFilter === "all" || type === approvalTypeFilter;
  };

  const fetchApprovals = async (filterType) => {
    const token = localStorage.getItem("token");
    const contactId = localStorage.getItem("contact_id");
    const userProfileID = localStorage.getItem("userProfileId");
    if (!token || !contactId) return;

    // tickets
    if (filterType === "tickets") {
      setLoading(true);
      try {
        const res = await fetch(
          `https://portal.gmacc.co.za/api/tickets/trustee/active/${userProfileID}/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!res.ok) {
          console.error("Error fetching tickets:", res.statusText);
          setApprovals((prev) => ({ ...prev, tickets: [] }));
          return;
        }
        const data = await res.json();
        setApprovals((prev) => ({ ...prev, tickets: data }));
      } catch (err) {
        console.error("Error fetching tickets:", err);
      } finally {
        setLoading(false);
      }
      return;
    }

    // buildings (trusted list)
    if (filterType === "buildings") {
      setLoading(true);
      const combinedBuildings = [];
      try {
        for (const buildingId of allowedBuildings) {
          const res = await fetch(
            "https://portal.gmacc.co.za/api/trustee/all-approvals/buildings/",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ building_id: buildingId }),
            }
          );

          const text = await res.text();
          if (!res.ok) {
            console.error("API error for", buildingId, text);
            continue;
          }

          const data = JSON.parse(text);
          if (data?.buildings?.length) {
            combinedBuildings.push(...data.buildings);
          }
        }

        setBuildings(combinedBuildings);
        console.log("Fetched Buildings:", combinedBuildings);
        localStorage.setItem(
          "trustee_buildings",
          JSON.stringify(combinedBuildings)
        );
      } catch (err) {
        console.error("Error fetching buildings individually:", err);
      } finally {
        setLoading(false);
      }
      return;
    }

    // approvals
    const endpoints = {
      active: "https://portal.gmacc.co.za/api/trustee/approvals/active/",
      history: "https://portal.gmacc.co.za/api/trustee/approvals/history/",
      approved: "https://portal.gmacc.co.za/api/trustee/approvals/approved/",
      declined: "https://portal.gmacc.co.za/api/trustee/approvals/declined/",
      abstained: "https://portal.gmacc.co.za/api/trustee/approvals/abstained/",
    };

    try {
      setLoading(true);
      const res = await fetch(endpoints[filterType], {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ contact_id: contactId }),
      });
      const data = await res.json();
      setApprovals((prev) => ({
        ...prev,
        [filterType]: data[filterType] || [],
      }));

      if (filterType === "active") {
        localStorage.setItem(
          "trustee_active_individual_approvals",
          JSON.stringify(data.active || [])
        );
      }
      if (filterType === "history") {
        localStorage.setItem(
          "trustee_history_individual_approvals",
          JSON.stringify(data.history || [])
        );
      }
    } catch (err) {
      console.error("Error fetching approvals:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllowedBuildings();
  }, []);

  useEffect(() => {
    document.title = "GMA Portal || Trustee Portal";
  }, []);

  // pull user access for top-right dropdown
  useEffect(() => {
    const fetchUserAccess = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      try {
        const decoded = JSON.parse(atob(token.split(".")[1]));
        const { email, contact_id, firstname, lastname, name } = decoded || {};

        // Set display name safely
        const fullName =
          firstname && lastname
            ? `${firstname} ${lastname}`
            : name
            ? name
            : email || "User";

        setDisplayName(fullName);

        if (!email) {
          setLoadingAccess(false);
          return;
        }

        const resp = await fetch(
          "https://portal.gmacc.co.za/api/contact/determine-access/",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ email, contact_id }),
          }
        );

        const data = await resp.json();
        if (resp.ok) {
          setUserAccess(data.access || []);
        }
      } catch (err) {
        console.error("Failed to fetch access:", err);
      } finally {
        setLoadingAccess(false);
      }
    };

    fetchUserAccess();
  }, [navigate]);

  // when filters change, get data
  useEffect(() => {
    if (mainFilter === "approvals") {
      if (allowedBuildings.length > 0) {
        fetchApprovals(subFilter);
      }
    } else {
      if (allowedBuildings.length > 0 || mainFilter === "tickets") {
        fetchApprovals(mainFilter);
      }
    }
  }, [allowedBuildings, mainFilter, subFilter]);

  // reset filters on tab change
  useEffect(() => {
    setSearchTerm("");
    setDateFilter({ from: "", to: "" });
    setPendingDateFilter({ from: "", to: "" });
    setApprovalTypeFilter("all");
    setCurrentPage(1);
    setExpandedBuilding(null);
  }, [mainFilter, subFilter]);

  // figure out which dataset we’re working with
  const filterVar = mainFilter === "approvals" ? subFilter : mainFilter;

  let filteredData = {};
  if (filterVar === "tickets") {
    const rawTickets = approvals.tickets || [];
    const filteredTickets = rawTickets.filter((ticket) => {
      const num = String(ticket.cr446_ticketnumber || "").toLowerCase();
      const desc = String(ticket.cr446_ticketdescription || "").toLowerCase();
      const createdOn =
        ticket.createdon ||
        ticket["createdon@OData.Community.Display.V1.FormattedValue"];
      const matchesSearch =
        num.includes(searchTerm.toLowerCase()) ||
        desc.includes(searchTerm.toLowerCase());
      return matchesSearch && matchesDateFilter(createdOn);
    });
    filteredData = groupByBuilding(filteredTickets);
  } else if (filterVar !== "buildings") {
    const rawData = approvals[filterVar];
    let approvalsList = [];
    if (Array.isArray(rawData)) {
      approvalsList = rawData;
    } else if (rawData?.value && Array.isArray(rawData.value)) {
      approvalsList = rawData.value;
    } else if (rawData?.results && Array.isArray(rawData.results)) {
      approvalsList = rawData.results;
    }

    filteredData = groupByBuilding(
      approvalsList.filter(
        (item) =>
          allowedBuildings
            .map((guid) => guid.toLowerCase())
            .includes(item.buildingGuid?.toLowerCase()) &&
          item.subject?.toLowerCase().includes(searchTerm.toLowerCase()) &&
          matchesDateFilter(item.date) &&
          matchesTypeFilter(item.approvalType)
      )
    );
  }

  const handleAction = async (approvalId, actionType) => {
    const token = localStorage.getItem("token");
    if (!token) {
      alert("No token found. Please log in again.");
      navigate("/");
      return;
    }
    const endpointMap = {
      approve: "approve",
      decline: "decline",
      abstain: "abstain",
    };
    try {
      const response = await fetch(
        `https://portal.gmacc.co.za/api/trustee/approval/${endpointMap[actionType]}/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ approval_id: approvalId }),
        }
      );
      if (response.ok) {
        if (mainFilter === "approvals") {
          fetchApprovals(subFilter);
        }
      } else {
        const errorData = await response.json();
        alert(`Failed: ${errorData.error || "Unknown error"}`);
      }
    } catch (error) {
      console.error("API error:", error);
      alert("An error occurred. Please try again.");
    }
  };
  const [displayName, setDisplayName] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Default sidebar state based on device width
  useEffect(() => {
  const handleResize = () => {
    const desktop = window.innerWidth >= 1024;
    setIsDesktop(desktop);

    // Only force closed when moving to smaller screens.
    // On desktop, keep whatever state the user last chose.
    if (!desktop) {
      setSidebarOpen(false);
    }
  };

  handleResize();
  window.addEventListener("resize", handleResize);

  return () => window.removeEventListener("resize", handleResize);
}, []);

  useEffect(() => {
  function handleClick(e) {
    // On desktop/laptop, only the burger should control the sidebar
    if (isDesktop) return;

    const sidebar = document.getElementsByClassName(styles.sidebar)[0];
    const hamburger = document.getElementsByClassName(styles.menuBtn)[0];

    if (!sidebar || !hamburger) return;

    const clickedInsideSidebar = sidebar.contains(e.target);
    const clickedHamburger = hamburger.contains(e.target);

    if (sidebarOpen && !clickedInsideSidebar && !clickedHamburger) {
      setSidebarOpen(false);
    }
  }

  document.addEventListener("click", handleClick);
  return () => document.removeEventListener("click", handleClick);
}, [sidebarOpen, isDesktop]);

  return (
    <div className={styles.page}>
      {/* top bar */}
      <header className={styles.topbar}>
        <div className={styles.topbarLeft}>
          <img src={logoimage} alt="GMA Logo" className={styles.topLogo} />

          {/* HAMBURGER: opens/closes sidebar */}
          <button
            type="button"
            className={styles.menuBtn}
            onClick={() => setSidebarOpen((v) => !v)}
          >
            ☰
          </button>

          <h1 className={styles.topTitle}>TRUSTEE PORTAL</h1>
        </div>

        {/* USER CHIP: opens dropdown */}
        <div
          className={styles.userChip}
          onClick={() => {
            navigate("/user-profile");
          }}
          style={{ cursor: "pointer" }}
        >
          <span className={styles.statusDot}></span>
          <span className={styles.userName}>{displayName} ▾</span>
        </div>
      </header>

      <div className={styles.shell}>
        {/* left sidebar */}
        <PortalSidebar
  activePortal="trustee"
  activeTab={mainFilter}
  onTabChange={setMainFilter}
  userAccess={userAccess}
  loadingAccess={loadingAccess}
  sidebarOpen={sidebarOpen}
/>

        {/* right content */}
        <main className={styles.contentArea}>
          <div className={styles.panel}>
            {/* approvals sub tabs */}
            {mainFilter === "approvals" && (
              <div className={styles.subTabs}>
                <button
                  className={`${styles.subTab} ${
                    subFilter === "active" ? styles.subTabActive : ""
                  }`}
                  onClick={() => setSubFilter("active")}
                >
                  ACTIVE
                </button>
                <button
                  className={`${styles.subTab} ${
                    subFilter === "history" ? styles.subTabActive : ""
                  }`}
                  onClick={() => setSubFilter("history")}
                >
                  HISTORY
                </button>
                <button
                  className={`${styles.subTab} ${
                    subFilter === "approved" ? styles.subTabActive : ""
                  }`}
                  onClick={() => setSubFilter("approved")}
                >
                  APPROVED
                </button>
                <button
                  className={`${styles.subTab} ${
                    subFilter === "declined" ? styles.subTabActive : ""
                  }`}
                  onClick={() => setSubFilter("declined")}
                >
                  DECLINED
                </button>
                <button
                  className={`${styles.subTab} ${
                    subFilter === "abstained" ? styles.subTabActive : ""
                  }`}
                  onClick={() => setSubFilter("abstained")}
                >
                  ABSTAINED
                </button>
              </div>
            )}

            {filterVar !== "buildings" && (
              <p className={styles.maintp}>
                {filterVar === "active" &&
                  "These are approvals you have not yet made a decision on. You can vote to approve, decline, or abstain them."}

                {filterVar === "history" &&
                  "These show your full history of approvals regardless of outcome."}

                {filterVar === "approved" &&
                  "These are the approvals where you voted to approve."}

                {filterVar === "declined" &&
                  "These are the approvals you voted to decline."}

                {filterVar === "abstained" &&
                  "These are the approvals where you abstained from voting."}

                {filterVar === "tickets" &&
                  "These are your active tickets, grouped by building."}
              </p>
            )}

            {/* filters (not for buildings) */}
            {filterVar !== "buildings" && (
              <div className={styles.filterRow}>
                <input
                  type="text"
                  placeholder="Search Subject"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />

                {filterVar !== "tickets" && (
                  <>
                    <select
                      className={styles.select}
                      value={dateMode}
                      onChange={(e) => {
                        setDateMode(e.target.value);
                        setPendingDateFilter({ from: "", to: "" });
                      }}
                    >
                      <option value="single">Single Date</option>
                      <option value="range">Date Range</option>
                    </select>

                    <div className={styles.dateGroup}>
                      <input
                        type="date"
                        className={styles.dateInput}
                        value={pendingDateFilter.from}
                        onChange={(e) =>
                          setPendingDateFilter((pd) => ({
                            ...pd,
                            from: e.target.value,
                          }))
                        }
                      />
                      {dateMode === "range" && (
                        <input
                          type="date"
                          className={styles.dateInput}
                          min={pendingDateFilter.from}
                          value={pendingDateFilter.to}
                          onChange={(e) =>
                            setPendingDateFilter((pd) => ({
                              ...pd,
                              to: e.target.value,
                            }))
                          }
                        />
                      )}
                    </div>

                    <select
                      className={styles.select}
                      value={approvalTypeFilter}
                      onChange={(e) => setApprovalTypeFilter(e.target.value)}
                    >
                      {approvalTypeOptions.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>

                    <button
                      className={styles.confirmBtn}
                      onClick={() => {
                        if (dateMode === "single" && pendingDateFilter.from) {
                          setDateFilter({ from: pendingDateFilter.from });
                        } else if (
                          dateMode === "range" &&
                          pendingDateFilter.from &&
                          pendingDateFilter.to
                        ) {
                          if (
                            new Date(pendingDateFilter.from) >
                            new Date(pendingDateFilter.to)
                          ) {
                            alert("From date cannot be after To date");
                            return;
                          }
                          setDateFilter({
                            from: pendingDateFilter.from,
                            to: pendingDateFilter.to,
                          });
                        }
                      }}
                    >
                      Confirm Date
                    </button>

                    <button
                      className={styles.clearBtn}
                      onClick={() => {
                        setSearchTerm("");
                        setDateFilter({ from: "", to: "" });
                        setPendingDateFilter({ from: "", to: "" });
                        setApprovalTypeFilter("all");
                      }}
                    >
                      Clear Filters
                    </button>
                  </>
                )}
              </div>
            )}

            {/* main listing */}
            {filterVar === "buildings" ? (
              loading ? (
                <div className={styles.loadingContainer}>
                  <div className={styles.loadingImage}></div>
                  <p className={styles.loadingText}>Loading...</p>
                </div>
              ) : buildings.length === 0 ? (
                <p className={styles.errorText}>No Buildings Found.</p>
              ) : (
                buildings.map((b) => (
                  <div
                    key={b.buildingGuid}
                    className={styles.buildingStrip}
                    onClick={() => navigate(`/building-info/${b.buildingGuid}`)}
                  >
                    <span className={styles.buildingName}>{b.building}</span>
                    <button
                      className={styles.buildingViewBtn}
                      onClick={() =>
                        navigate(`/building-info/${b.buildingGuid}`)
                      }
                    >
                      View Building
                    </button>
                  </div>
                ))
              )
            ) : loading ? (
              <div className={styles.loadingContainer}>
                <div className={styles.loadingImage}></div>
                <p className={styles.loadingText}>Loading...</p>
              </div>
            ) : Object.keys(filteredData).length === 0 ? (
              <p className={styles.errorText}>
                {mainFilter === "tickets"
                  ? "No Tickets Found."
                  : "No Approvals Found."}
              </p>
            ) : (
              Object.entries(filteredData).map(([building, group], index) => {
                const startIndex = (currentPage - 1) * recordsPerPage;
                const paginatedItems = group.items.slice(
                  startIndex,
                  startIndex + recordsPerPage
                );
                return (
                  <div key={building} className={styles.approvalBlock}>
                    <div
                      className={styles.approvalHeader}
                      onClick={() =>
                        setExpandedBuilding(
                          expandedBuilding === index ? null : index
                        )
                      }
                    >
                      <span className={styles.approvalBuilding}>
                        {building}
                      </span>
                      <div className={styles.headerActions}>
                        {group.buildingGuid && (
                          <button
                            className={styles.headerBtnLight}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/building-info/${group.buildingGuid}`);
                            }}
                          >
                            View Building
                          </button>
                        )}
                        <button className={styles.headerBtnLight}>
                          {expandedBuilding === index
                            ? "Show less ▲"
                            : "Show more ▼"}
                        </button>
                      </div>
                    </div>

                    {expandedBuilding === index && (
                      <>
                        <div className={styles.responsiveTableWrapper}>
                          <table className={styles.dataTable}>
                            <thead>
                              {filterVar === "tickets" ? (
                                <tr>
                                  <th>Ticket Number</th>
                                  <th>Progress</th>
                                  <th>Description</th>
                                  <th>Created Date</th>
                                </tr>
                              ) : (
                                <tr>
                                  <th>Subject:</th>
                                  <th>Approval Type:</th>
                                  <th>Approval Count:</th>
                                  <th>Required Count:</th>
                                  <th>File:</th>
                                  <th>
                                    {filterVar === "active"
                                      ? "Created Date:"
                                      : "Response Date:"}
                                  </th>
                                  {filterVar === "active" && <th>Actions</th>}
                                </tr>
                              )}
                            </thead>
                            <tbody>
                              {filterVar === "tickets"
                                ? paginatedItems.map((ticket) => {
                                    const id = ticket.cr446_loggedticketid;
                                    const number = ticket.cr446_ticketnumber;
                                    const progress =
                                      ticket[
                                        "cr446_progress@OData.Community.Display.V1.FormattedValue"
                                      ];
                                    const description =
                                      ticket.cr446_ticketdescription;
                                    const created =
                                      ticket[
                                        "createdon@OData.Community.Display.V1.FormattedValue"
                                      ] || ticket.createdon;

                                    return (
                                      <tr key={id}>
                                        <td
                                          className={styles.linkCell}
                                          onClick={() =>
                                            navigate(`/loggedticket/${id}`)
                                          }
                                        >
                                          {number}
                                        </td>
                                        <td>{progress}</td>
                                        <td>{description}</td>
                                        <td>{formatDateSafe(created)}</td>
                                      </tr>
                                    );
                                  })
                                : paginatedItems.map((item) => (
                                    <tr key={item.id}>
                                      <td
                                        className={styles.linkCell}
                                        onClick={() =>
                                          navigate(
                                            `/building-approvals/${item.approvalId}`
                                          )
                                        }
                                      >
                                        {item.subject}
                                      </td>
                                      <td>{item.approvalType}</td>
                                      <td>{item.approvalCount}</td>
                                      <td>{item.requiredCount}</td>
                                      <td>
                                        <a
                                          href={item.fileLink}
                                          target="_blank"
                                          rel="noreferrer"
                                          className={styles.viewFileBtn}
                                        >
                                          View File
                                        </a>
                                      </td>
                                      <td>{formatDateSafe(item.date)}</td>
                                      {filterVar === "active" && (
                                        <td>
                                          <div className={styles.actionBtns}>
                                            <button
                                              className={styles.approveBtn}
                                              onClick={() =>
                                                handleAction(item.id, "approve")
                                              }
                                            >
                                              Approve
                                            </button>
                                            <button
                                              className={styles.declineBtn}
                                              onClick={() =>
                                                handleAction(item.id, "decline")
                                              }
                                            >
                                              Decline
                                            </button>
                                            <button
                                              className={styles.abstainBtn}
                                              onClick={() =>
                                                handleAction(item.id, "abstain")
                                              }
                                            >
                                              Abstain
                                            </button>
                                          </div>
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                            </tbody>
                          </table>
                        </div>

                        <Pagination
                          currentPage={currentPage}
                          setCurrentPage={setCurrentPage}
                          totalRecords={group.items.length}
                          recordsPerPage={recordsPerPage}
                        />
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default TrusteePortalPage;
